import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { DBRequest, useConfig } from '../../api/api';
import { ProjectGridSkeletons } from './project-grid/ProjectGridSkeletons';
import CardGrid from '../../components/cards/layout/CardGrid';
import { ProjectGridItem } from './project-grid/ProjectGridItem';
import { ProjectListItem } from './project-list/ProjectListItem';
import Pagination from '../../components/special/Pagination';
import { ProjectAddElementButton } from '../../components/exo/project/ProjectAddElementButton';
import NewEmptyProject from '../../components/new/NewEmptyProject';
import ExoDialog from '../../components/exo/ExoDialog';
import { updateOrCreate } from '../../components/special/updateOrCreate';
import { hasPermissions } from '../../auth/hasPermissions';
import { useNavigate } from 'react-router-dom';
import NoDataAnimation from '../../components/animation/NoDataAnimation';

export const ProjectsGrid = ({
  projectStatus,
  filterString = '',
  viewMode = 'grid',
  hideCreate = false,
  topContent = null,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const [apiData, setApiData] = useState(null);

  const navigate = useNavigate();

  const typeMap = {
    own: 'status[eq]=0',
    active: 'status[eq]=0',
    archive: 'status[ne]=0',
  };

  function buildQuery() {
    const statusParam = typeMap[projectStatus];
    const trimmed = (filterString || '').replace(/&$/, '');
    return trimmed ? `${statusParam}&${trimmed}` : statusParam;
  }

  useEffect(() => {
    const query = buildQuery();
    DBRequest({
      config,
      path:
        projectStatus === 'own'
          ? `projects/own?${query}`
          : `projects?${query}`,
      onResponse: setApiData,
    });
  }, [projectStatus, config, filterString]);

  const [open, setOpen] = useState(false);

  function handleResponse(data) {
    setApiData(updateOrCreate(apiData, data));
    navigate(`/projects/management/${data.id}`);
  }

  return (
    <Box className="h-full flex flex-col overflow-hidden gap-2 justify-between">
      <Box className="flex flex-col gap-2 h-full overflow-auto pb-4">
        {topContent}
        {viewMode === 'list' ? (
          <Box className="flex flex-col gap-2">
            {!apiData ? (
              <ProjectGridSkeletons n={5} />
            ) : (
              <>
                {!hideCreate && hasPermissions('project', 'write') && (
                  <ProjectAddElementButton
                    label={t('New Project')}
                    onClick={() => setOpen(true)}
                    size="small"
                    className="w-full"
                    sx={{ height: '56px', padding: '8px 12px' }}
                  />
                )}
                {apiData.data.map((item) => (
                  <ProjectListItem key={item.id} project={item} />
                ))}
                {apiData.data.length === 0 && (
                  <Box className="w-full flex justify-center">
                    <Box
                      className="flex items-center justify-center rounded-lg p-4 backdrop-blur-lg"
                      sx={{
                        bgcolor: colors.card,
                        aspectRatio: '4 / 3',
                        width: '17.5rem',
                        maxWidth: '100%',
                      }}
                    >
                      <NoDataAnimation size="60%" />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        ) : (
          <>
            <CardGrid>
              {!apiData ? (
                <ProjectGridSkeletons n={5} />
              ) : (
                <>
                  {!hideCreate && hasPermissions('project', 'write') && (
                    <ProjectAddElementButton
                      label={t('New Project')}
                      onClick={() => setOpen(true)}
                    />
                  )}
                  {apiData.data.map((item) => (
                    <ProjectGridItem key={item.id} project={item} />
                  ))}
                </>
              )}
            </CardGrid>
            {apiData && apiData.data.length === 0 && (
              <Box className="w-full flex justify-center">
                <Box
                  className="flex items-center justify-center rounded-lg p-4 backdrop-blur-lg"
                  sx={{
                    bgcolor: colors.card,
                    aspectRatio: '4 / 3',
                    width: '17.5rem',
                    maxWidth: '100%',
                  }}
                >
                  <NoDataAnimation size="60%" />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      <Box sx={{ bgcolor: colors.glass }} className="rounded-lg">
        {apiData && (
          <Pagination
            data={apiData}
            setData={setApiData}
            filter={{ name: 'name', filter: buildQuery() }}
            setLoading={() => {}}
          />
        )}
      </Box>
      <ExoDialog open={open} onClose={() => setOpen(false)}>
        <NewEmptyProject
          onClose={() => setOpen(false)}
          onCreate={(data) => {
            setOpen(false);
            handleResponse(data);
          }}
        />
      </ExoDialog>
    </Box>
  );
};
